import { useState } from "react";
import { X, Printer, AlertTriangle, CheckCircle2, Link2, Eye, Copy, CreditCard, BellRing, Receipt, Coins, Trash2 } from "lucide-react";
import { InvoicePaper } from "./BusinessViews.jsx";
import { DeliverBox } from "./Deliver.jsx";
import { pdfFile, safeFilename, printPdf } from "./deliver.js";
import { invoiceChecks, buildInvoiceEmail, buildReminderEmail, buildReceiptEmail, receiptNumber, invoiceTotals, invoicePaidAmount, invoiceBalance, localToday, effectiveStatus, daysOverdue, prettyDate } from "./biz.js";
import { invoiceLink } from "./remote.js";

const when = (iso) => (iso ? prettyDate(String(iso).slice(0, 10)) : "");

// Review before you print or send. Saved invoices get delivery, an online link (viewed + card payment) and overdue reminders.
export function InvoiceReview({ inv, client, profile, fmt, isDraft, ctx, onClose, onEdit, onSave, onMarkSent, onSaveEmail, onSavePaidMethod, onRecordPayment, onRemovePayment, onOpenDetails, onOpenPayments }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [printErr, setPrintErr] = useState("");
  const [accept, setAccept] = useState(!!inv.online?.acceptPayments);
  const [auto, setAuto] = useState(!!inv.online?.autoReminders);
  const [method, setMethod] = useState(inv.paidMethod || "");
  const [payAmt, setPayAmt] = useState(null); // null = untouched, so the field shows the balance; "" = the person cleared it
  const [payDate, setPayDate] = useState(localToday());
  const [payMethod, setPayMethod] = useState("");
  const [payErr, setPayErr] = useState("");
  const [rcptSel, setRcptSel] = useState(null); // payment id, or "all" for the full-invoice receipt
  const checks = invoiceChecks(inv, client, profile);
  const errors = checks.filter((c) => c.level === "error");
  const online = inv.online?.token ? inv.online : null;
  const link = online ? invoiceLink(online.token) : null;
  const status = effectiveStatus(inv);
  const overdue = status === "overdue";
  const canOnline = !isDraft && ctx?.signedIn && ctx?.config;
  const cardsReady = !!ctx?.stripe?.chargesEnabled;

  const branding = profile.business?.emailBranding !== false;
  const makeFile = async () => {
    const { invoicePdf } = await import("./pdfDocs.js");
    return pdfFile(await invoicePdf(inv, client, profile, { link, branding }), safeFilename(`${inv.number}.pdf`));
  };
  const pays = Array.isArray(inv.payments) ? inv.payments : [];
  const total = invoiceTotals(inv).total, paid = invoicePaidAmount(inv), balance = invoiceBalance(inv);
  const isPaid = inv.status === "paid";
  const sel = rcptSel === "all" ? (isPaid ? "all" : null) : pays.some((p) => p.id === rcptSel) ? rcptSel : (isPaid ? "all" : pays[pays.length - 1]?.id || null);
  const selPay = sel && sel !== "all" ? pays.find((p) => p.id === sel) : null;
  const makeReceipt = async () => {
    const { receiptPdf } = await import("./pdfDocs.js");
    return pdfFile(await receiptPdf(inv, client, profile, { branding, payment: selPay }), safeFilename(`${receiptNumber(inv, selPay || undefined)}.pdf`));
  };
  const receiptKey = JSON.stringify([inv.number, inv.items, inv.taxRate, inv.paidDate, inv.paidVia, inv.paidMethod, pays, sel, profile.business?.logo ? 1 : 0, client?.name, branding]);
  const receiptMail = buildReceiptEmail(inv, client, profile, fmt, selPay || undefined);
  const amtShown = payAmt === null ? String(balance) : payAmt;
  function addPayment() {
    const amount = Math.round((Number(amtShown) || 0) * 100) / 100;
    if (!(amount > 0)) return setPayErr("Enter the amount received.");
    if (amount > balance + 0.005) return setPayErr(`That's more than the ${fmt(balance)} still due.`);
    if (!payDate) return setPayErr("Choose the date it was received.");
    setPayErr("");
    onRecordPayment?.({ amount, date: payDate, method: payMethod });
    setPayAmt(null); setPayMethod(""); setRcptSel(null);
  }
  const fileKey = JSON.stringify([inv.number, inv.items, inv.taxRate, inv.status, inv.notes, inv.dueDate, link, profile.business?.logo ? 1 : 0, client?.name, branding]);
  const mail = buildInvoiceEmail(inv, client, profile, fmt, link);
  const reminder = buildReminderEmail(inv, client, profile, fmt, link);
  const replyTo = profile.business?.email;

  async function publish() {
    setBusy(true); setNote("");
    try { await ctx.publish(inv, client, { acceptPayments: accept && cardsReady, autoReminders: auto }); setNote(online ? "Updated." : "Link created."); }
    catch (e) { setNote(e.message); }
    setBusy(false);
  }
  async function unpublish() {
    setBusy(true);
    try { await ctx.unpublish(inv); setNote("Sharing stopped. The old link no longer works."); } catch (e) { setNote(e.message); }
    setBusy(false);
  }
  async function copy() { try { await navigator.clipboard.writeText(link); setNote("Link copied."); } catch (e) { setNote(link); } }

  return (
    <div className="overlay-backdrop fixed inset-0 z-40 flex items-end lg:items-center justify-center">
      <div className="bg-card2 rounded-t-3xl lg:rounded-3xl w-full max-w-sm md:max-w-xl p-5" style={{ maxHeight: "94vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="v-display text-lg font-semibold">{isDraft ? "Preview" : "Review"} {inv.number}</h3>
          <button onClick={onClose} aria-label="Close"><X size={18} className="text-ivory-dim" /></button>
        </div>
        <p className="text-xs text-ivory-dim mb-4">{isDraft ? "This is how the invoice will look. Nothing is saved until you choose Save." : "Check it over, then send or print. Nothing leaves this device until you do."}</p>

        {checks.length === 0 ? (
          <div className="flex items-center gap-2 text-xs rounded-xl px-3 py-2 mb-4" style={{ background: "var(--brass-dim)" }}><CheckCircle2 size={14} style={{ color: "var(--sage)" }} className="shrink-0" /> Nothing looks missing.</div>
        ) : (
          <div className="rounded-xl px-3 py-2.5 mb-4 flex flex-col gap-1.5" style={{ background: "var(--brass-dim)" }}>
            {checks.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" style={{ color: c.level === "error" ? "var(--rust)" : "var(--brass)" }} />
                <span className="flex-1">{c.text}{c.action === "details" && onOpenDetails && <> <button onClick={onOpenDetails} className="text-brass font-medium underline">Fix</button></>}</span>
              </div>
            ))}
          </div>
        )}

        {(online || overdue) && !isDraft && (
          <div className="flex flex-wrap gap-2 mb-3">
            {overdue && <span className="text-xs rounded-full px-2.5 py-1 font-medium" style={{ background: "rgba(181,68,95,0.16)", color: "var(--rust)" }}>Overdue {daysOverdue(inv)} days</span>}
            {online?.paidAt && <span className="text-xs rounded-full px-2.5 py-1 font-medium" style={{ background: "var(--brass-dim)", color: "var(--sage)" }}>Paid online {when(online.paidAt)}</span>}
            {online && !online.paidAt && (online.viewCount > 0
              ? <span className="text-xs rounded-full px-2.5 py-1 flex items-center gap-1" style={{ background: "var(--brass-dim)" }}><Eye size={12} /> Viewed {online.viewCount}× · first {when(online.firstViewedAt)}</span>
              : <span className="text-xs rounded-full px-2.5 py-1 text-ivory-dim" style={{ background: "var(--chip)" }}>Link not opened yet</span>)}
          </div>
        )}

        <InvoicePaper inv={inv} client={client} profile={profile} fmt={fmt} />

        <div className="mt-5 flex flex-col gap-3">
          <button onClick={() => { setPrintErr(""); printPdf(makeFile).catch((e) => setPrintErr(e.message || "Couldn't create the PDF")); }} disabled={errors.length > 0} className="w-full flex items-center justify-center gap-1.5 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-40"><Printer size={14} /> Print / Save as PDF</button>
          {printErr && <div className="text-xs" style={{ color: "var(--rust)" }}>{printErr}</div>}

          {!isDraft && (
            <DeliverBox kind="invoice" makeFile={makeFile} fileKey={fileKey} subject={mail.subject} body={mail.body} defaultTo={client?.email || ""} businessName={profile.business?.name || profile.name} replyTo={replyTo} ctx={ctx} invoiceId={inv.id} branding={branding}
              onRecipient={(e) => { if (client && e !== client.email) onSaveEmail?.(e); }}
              onSent={inv.status === "draft" ? () => onMarkSent?.() : undefined} />
          )}

          {!isDraft && (
            <div className="bg-card rounded-2xl p-3.5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm font-semibold"><Coins size={14} className="text-brass" /> Payments</div>
                <div className="text-xs font-medium" style={{ color: isPaid ? "var(--sage)" : paid > 0 ? "var(--brass)" : "var(--ivory-dim)" }}>{isPaid ? "Paid in full" : paid > 0 ? "Partly paid" : "Nothing received yet"}</div>
              </div>
              <div className="flex justify-between text-xs text-ivory-dim mb-2.5">
                <span>Paid {fmt(paid)} of {fmt(total)}</span>
                <span className="v-mono" style={{ color: balance > 0 ? "var(--ivory)" : "var(--sage)" }}>{balance > 0 ? `${fmt(balance)} due` : "No balance"}</span>
              </div>

              {pays.length > 0 && (
                <div className="mb-2.5">
                  {pays.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 py-1.5 border-b border-hair last:border-0 text-xs">
                      <div className="min-w-0 flex-1"><div>{when(p.date)}</div>{p.method && <div className="text-ivory-dim truncate">{p.method}</div>}</div>
                      <span className="v-mono">{fmt(p.amount)}</span>
                      <button onClick={() => onRemovePayment?.(p.id)} className="opacity-40 p-0.5" aria-label="Remove payment"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}

              {balance > 0 && (
                <div>
                  <div className="text-xs text-ivory-dim mb-1.5">{pays.length ? "Record another payment" : "Record a payment (full or part)"}</div>
                  <div className="flex gap-2 mb-2">
                    <input value={amtShown} onChange={(e) => setPayAmt(e.target.value)} type="number" inputMode="decimal" step="0.01" min="0" aria-label="Amount received" className="w-1/2 min-w-0 rounded-lg px-3 py-2 text-sm v-mono" />
                    <input value={payDate} onChange={(e) => setPayDate(e.target.value)} type="date" aria-label="Date received" className="w-1/2 min-w-0 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <input value={payMethod} onChange={(e) => setPayMethod(e.target.value)} placeholder="Method (e-Transfer, cash, cheque…)" className="w-full rounded-lg px-3 py-2 text-sm mb-2" />
                  {payErr && <div className="text-xs mb-2" style={{ color: "var(--rust)" }}>{payErr}</div>}
                  <button onClick={addPayment} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm">Record payment</button>
                </div>
              )}
            </div>
          )}

          {!isDraft && (isPaid || pays.length > 0) && (
            <div className="bg-card rounded-2xl p-3.5">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Receipt size={14} className="text-brass" /> Receipt {receiptNumber(inv, selPay || undefined)}</div>
              <div className="text-xs text-ivory-dim mb-2.5">{selPay ? `A receipt for the ${fmt(selPay.amount)} received on ${when(selPay.date)}.` : `This invoice is paid${inv.paidDate ? ` (${when(inv.paidDate)})` : ""}. Give your client a receipt as proof of payment.`}</div>
              {pays.length > 0 && (
                <div className="mb-2.5">
                  <label className="text-xs text-ivory-dim" htmlFor="rcpt-for">Receipt for</label>
                  <select id="rcpt-for" value={sel || ""} onChange={(e) => setRcptSel(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm mt-1">
                    {pays.map((p, i) => <option key={p.id} value={p.id}>Payment {i + 1} · {fmt(p.amount)} · {when(p.date)}</option>)}
                    {isPaid && pays.length > 1 && <option value="all">All payments (full receipt)</option>}
                  </select>
                </div>
              )}
              {!selPay && pays.length === 0 && inv.paidVia !== "online" && (
                <div className="mb-2.5">
                  <label className="text-xs text-ivory-dim" htmlFor="paid-method">Payment method (optional)</label>
                  <input id="paid-method" value={method} onChange={(e) => setMethod(e.target.value)} onBlur={() => { if (method.trim() !== (inv.paidMethod || "")) onSavePaidMethod?.(method); }} placeholder="e-Transfer, cash, cheque…" className="w-full rounded-lg px-3 py-2 text-sm mt-1" />
                </div>
              )}
              <button onClick={() => { setPrintErr(""); if (!selPay && pays.length === 0 && method.trim() !== (inv.paidMethod || "")) onSavePaidMethod?.(method); printPdf(makeReceipt).catch((e) => setPrintErr(e.message || "Couldn't create the PDF")); }} className="w-full flex items-center justify-center gap-1.5 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm mb-3"><Printer size={14} /> Print / Save receipt</button>
              <DeliverBox kind="receipt" label="Email receipt to" makeFile={makeReceipt} fileKey={receiptKey} subject={receiptMail.subject} body={receiptMail.body} defaultTo={client?.email || ""} businessName={profile.business?.name || profile.name} replyTo={replyTo} ctx={ctx} invoiceId={inv.id} branding={branding}
                onRecipient={(e) => { if (client && e !== client.email) onSaveEmail?.(e); }} />
            </div>
          )}

          {!isDraft && overdue && (
            <div className="bg-card rounded-2xl p-3.5">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1"><BellRing size={14} className="text-brass" /> Overdue reminder</div>
              <div className="text-xs text-ivory-dim mb-2.5">{{ friendly: "A friendly nudge, since it's only just late.", firm: "A firmer note, as it's a few weeks late.", final: "A final reminder, as it's well overdue.", upcoming: "A reminder." }[reminder.tone]}{inv.reminders?.length ? ` Last reminder: ${when(inv.reminders[inv.reminders.length - 1].date)}.` : ""}</div>
              <DeliverBox kind="reminder" label="Send reminder to" makeFile={makeFile} fileKey={fileKey + "r"} subject={reminder.subject} body={reminder.body} defaultTo={client?.email || ""} businessName={profile.business?.name || profile.name} replyTo={replyTo} ctx={ctx} invoiceId={inv.id} branding={branding}
                onRecipient={(e) => { if (client && e !== client.email) onSaveEmail?.(e); }}
                onSent={(via) => ctx.logReminder(inv.id, via, reminder.tone)} />
            </div>
          )}

          {canOnline && (
            <div className="bg-card rounded-2xl p-3.5">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Link2 size={14} className="text-brass" /> Online invoice</div>
              <div className="text-xs text-ivory-dim mb-2.5">A private link your client opens in any browser. You see when it's been viewed{cardsReady ? ", and they can pay by card" : ""}.</div>
              {online && (
                <div className="flex gap-2 mb-2.5">
                  <input readOnly value={link} className="flex-1 min-w-0 rounded-lg px-3 py-2 text-xs v-mono" onFocus={(e) => e.target.select()} />
                  <button onClick={copy} className="flex items-center gap-1 bg-card2 rounded-lg px-3 text-xs font-medium"><Copy size={12} /> Copy</button>
                </div>
              )}
              {!online?.paidAt && (
                <>
                  {cardsReady ? (
                    <label className="flex items-start gap-2.5 text-xs mb-2 cursor-pointer"><input type="checkbox" className="mt-0.5" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
                      <span className="flex items-center gap-1"><CreditCard size={12} /> Let the client pay by card<span className="text-ivory-dim"> · fee: {ctx.config.fee.percent}% plus Stripe's</span></span></label>
                  ) : (
                    <button onClick={onOpenPayments} className="text-xs text-brass font-medium mb-2 flex items-center gap-1"><CreditCard size={12} /> Set up card payments →</button>
                  )}
                  <label className="flex items-start gap-2.5 text-xs mb-2.5 cursor-pointer" style={{ opacity: ctx.isPaid && ctx.config.emailEnabled ? 1 : 0.55 }}>
                    <input type="checkbox" className="mt-0.5" checked={auto && ctx.isPaid} disabled={!(ctx.isPaid && ctx.config.emailEnabled)} onChange={(e) => setAuto(e.target.checked)} />
                    <span>Email the client automatic reminders when it's overdue<span className="block text-ivory-dim">{ctx.isPaid ? (client?.email ? "Up to 3: a day late, then a week and two weeks after that." : "Add the client's email above first.") : "Paid accounts only."}</span></span></label>
                  <div className="flex gap-2">
                    <button onClick={publish} disabled={busy || errors.length > 0} className="flex-1 bg-brass text-ink font-semibold rounded-full py-2 text-sm disabled:opacity-40">{busy ? "Working…" : online ? "Update link" : "Create link"}</button>
                    {online && <button onClick={unpublish} disabled={busy} className="px-4 text-xs text-ivory-dim">Stop sharing</button>}
                  </div>
                </>
              )}
              {note && <div className="text-xs mt-2 text-ivory-dim">{note}</div>}
              <div className="text-xs text-ivory-dim mt-2.5 leading-relaxed">Creating a link uploads this one invoice (lines, your business header, the client's name and email) so the page can open. Nothing else leaves your device.</div>
            </div>
          )}
          {!isDraft && !ctx?.signedIn && ctx?.available && <div className="text-xs text-ivory-dim px-1">Sign in under Settings → Account &amp; sync to create online links with viewed tracking and card payments.</div>}

          <div className="flex gap-2">
            {isDraft ? (<>
              <button onClick={onClose} className="flex-1 bg-card rounded-full py-2.5 text-sm font-medium">Keep editing</button>
              <button onClick={onSave} disabled={errors.length > 0} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-40">Save invoice</button>
            </>) : (<>
              <button onClick={onEdit} className="flex-1 bg-card rounded-full py-2.5 text-sm font-medium">Edit invoice</button>
              <button onClick={onClose} className="flex-1 bg-card rounded-full py-2.5 text-sm font-medium">Close</button>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}
